/**
 * The admin table registry, fetched from the API.
 *
 * Nothing about the tables is baked into this bundle: the backend owns the
 * registry and serves it from /admin/scrud/_manifest, so a frontend and a
 * backend on different deploys can never disagree about what tables exist
 * or which columns they carry. Fetched once per page load and shared.
 */
import { useEffect, useState } from "react";
import adminApi from "@/api/adminApi";

let cache = null;

/**
 * Fill in anything an older backend's manifest doesn't send.
 *
 * backend.yml and frontend.yml both fire on a push to main and finish at
 * their own pace — the App Service deploy is much slower than the Static Web
 * App one — so for a few minutes after every release this bundle is talking
 * to the previous backend. Without these fallbacks that window is a crash
 * (`config.summary` undefined) on every data table. With them it is invisible.
 */
function normalise(entry) {
    const columns = (entry.columns ?? []).map((c) =>
        typeof c === "string" ? { name: c } : c
    );
    return {
        ...entry,
        columns,
        // `table` is what the pre-registry manifest called it.
        title: entry.title || entry.table || humanLabel(entry.routePath),
        summary: entry.summary?.length
            ? entry.summary
            : columns.slice(0, 5).map((c) => c.name),
        sourceCollection: entry.sourceCollection ?? null,
    };
}

function load() {
    if (!cache) {
        cache = adminApi
            .manifest()
            .then((tables) => (tables ?? []).map(normalise))
            .catch((e) => {
                cache = null; // let the next mount retry rather than cache a failure
                throw e;
            });
    }
    return cache;
}

export function useScrudManifest() {
    const [state, setState] = useState({ tables: null, error: null, loading: true });

    useEffect(() => {
        let alive = true;
        load().then(
            (tables) => alive && setState({ tables, error: null, loading: false }),
            (error) => alive && setState({ tables: null, error, loading: false })
        );
        return () => {
            alive = false;
        };
    }, []);

    return state;
}

/** Presentation is derived from the value, never declared. */
export function inferType(value) {
    if (value === null || value === undefined) return "text";
    if (typeof value === "boolean") return "checkbox";
    if (typeof value === "number") return "number";
    if (typeof value === "object") return "json";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(value)) return "date";
    return "text";
}

/** `stripe_coupon_id` -> `Stripe Coupon Id`, `PlanCode` -> `Plan Code`. */
export function humanLabel(name) {
    return name
        .replace(/[_-]+/g, " ")
        .replace(/(?<=[a-z0-9])(?=[A-Z])/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => (w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(" ");
}
